import { Statistic, message } from 'antd';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS } from "chart.js/auto";
import moment from 'moment';

export default function Stats({ month, monthText }) {
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await axios.get('https://fakestoreapi.com/products');
                const transactions = res.data;

                // Simulate dateOfSale and sold fields
                const enhancedTransactions = transactions.map(transaction => ({
                    ...transaction,
                    sold: Math.random() < 0.5, // Random boolean for sold status
                    dateOfSale: moment().subtract(Math.floor(Math.random() * 12), 'months').format() // Random date within last 12 months
                }));

                // Filter transactions based on the selected month
                const filteredTransactions = enhancedTransactions.filter(transaction => {
                    const saleDate = moment(transaction.dateOfSale);
                    return saleDate.month() === month - 1; // Adjust for zero-indexed month
                });

                // Calculate totals
                const totalSale = filteredTransactions.reduce((total, transaction) => total + transaction.price, 0);
                const soldCount = filteredTransactions.filter(transaction => transaction.sold).length;
                const unsoldCount = filteredTransactions.length - soldCount;

                // Prepare data for charts
                const barChartData = {}; // Prepare your data structure as needed
                const pieChartData = {}; // Prepare your data structure as needed

                // Example logic to prepare bar and pie chart data
                filteredTransactions.forEach(transaction => {
                    const category = transaction.category;
                    barChartData[category] = (barChartData[category] || 0) + 1; // Count products per category

                    // For pie chart, you might want to aggregate differently
                    pieChartData[category] = (pieChartData[category] || 0) + transaction.price;
                });

                setData({
                    statsData: {
                        totalSale,
                        soldCount,
                        unsoldCount
                    },
                    barChartData,
                    pieChartData
                });

                message.success('Data loaded successfully');
            } catch (error) {
                console.error(error);
                message.error('Error loading data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => setData(null);
    }, [month]);

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600' }}>Statistics for {monthText}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px', marginTop: '20px' }}>
                <Totals stats={data?.statsData} loading={loading} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '48px', maxWidth: '1200px', width: '100%' }}>
                    {data && <><BarChart data={data.barChartData} /><PieChart data={data.pieChartData} /></>}
                </div>
            </div>
        </div>
    );
}

const Totals = ({ stats, loading }) => (
    <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', maxWidth: '900px', padding: '20px', borderRadius: '8px', backgroundColor: '#f5f5f5', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
        <Statistic valueStyle={{ fontSize: '28px', fontWeight: '500', color: '#4caf50' }} title="Total Sale" value={stats?.totalSale} loading={loading} prefix="₹" />
        <Statistic valueStyle={{ fontSize: '28px', fontWeight: '500', color: '#2196f3' }} title="Total Sold Items" value={stats?.soldCount} loading={loading} />
        <Statistic valueStyle={{ fontSize: '28px', fontWeight: '500', color: '#f44336' }} title="Total Unsold Items" value={stats?.unsoldCount} loading={loading} />
    </div>
);

const BarChart = ({ data }) => {
    const chartData = {
        labels: Object.keys(data),
        datasets: [{
            label: 'Products',
            data: Object.values(data),
            backgroundColor: 'rgba(0, 105, 100, 0.7)',
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Number of Products per Category' },
        },
        scales: {
            x: { stacked: true, title: { display: true, text: 'Category' } },
            y: { stacked: true, title: { display: true, text: 'Product Count' }, ticks: { stepSize: 1 } },
        },
        aspectRatio: 1.5,
    };

    return <Bar data={chartData} options={options} style={{ width: '100%', maxHeight: '400px' }} />;
};

const PieChart = ({ data }) => {
    const chartData = {
        labels: Object.keys(data),
        datasets: [{
            label: 'Product Distribution',
            data: Object.values(data),
            backgroundColor: ['#4caf50', '#2196f3', '#ff9800', '#f44336'],
            hoverBackgroundColor: ['#66bb6a', '#42a5f5', '#ffa726', '#e57373'],
        }],
    };

    return (
        <Doughnut
            data={chartData}
            options={{
                responsive: true,
                plugins: {
                    legend: { position: 'bottom' },
                    title: { display: true, text: 'Product Distribution by Category' },
                },
            }}
            style={{ maxHeight: '400px', width: '100%', maxWidth: '500px' }}
        />
    );
};
